import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from './components/ui/toaster'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import CookieConsent from './components/CookieConsent'
import { Suspense, lazy } from 'react'

// Lazy load all pages for code splitting
const Login = lazy(() => import('./pages/Login'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const NewExtractPage = lazy(() => import('./pages/NewExtractPage'))
const FileManager = lazy(() => import('./pages/FileManager'))
const DownloadManager = lazy(() => import('./pages/DownloadManager'))
const History = lazy(() => import('./pages/History'))
const HistoryChat = lazy(() => import('./pages/HistoryChat'))
const Landing = lazy(() => import('./pages/Landing'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const Terms = lazy(() => import('./pages/Terms'))
const AccountSettings = lazy(() => import('./pages/AccountSettings'))
const Billing = lazy(() => import('./pages/Billing'))
const AdminDemo = lazy(() => import('./pages/AdminDemo'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
)

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute redirectTo="/landing">
                      <Navigate to="/dashboard" replace />
                    </ProtectedRoute>
                  }
                />
                <Route path="/landing" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="extract" element={<NewExtractPage />} />
                <Route path="history" element={<History />} />
                <Route path="history/:bulkJobId/chat" element={<HistoryChat />} />
                <Route path="chat" element={<History />} />
                <Route path="chat/:bulkJobId" element={<HistoryChat />} />
                <Route path="files" element={<FileManager />} />
                <Route path="downloads" element={<DownloadManager />} />
                <Route path="account" element={<AccountSettings />} />
                <Route path="billing" element={<Billing />} />
                <Route
                  path="admin/demo"
                  element={
                    <AdminRoute>
                      <AdminDemo />
                    </AdminRoute>
                  }
                />
              </Route>
            </Routes>
            </Suspense>
            <Toaster />
            <CookieConsent />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
